import Analytics, { type AnalyticsInstance, type AnalyticsPlugin } from 'analytics';
import mixpanelPlugin from '@analytics/mixpanel';
import googleTagManager from '@analytics/google-tag-manager';
import { bootstrapMixpanelSdk } from './plugins/mixpanelSdk';
import { IS_E2E, createE2EPlugin } from '@/test-support/e2e-mode';

/**
 * `analytics` (getanalytics.io) is the engine — it queues calls until plugins
 * load, persists identity, and fans every call out to the destination plugins.
 * Destinations are independently env-gated: Mixpanel via `VITE_MIXPANEL_TOKEN`,
 * GTM via `VITE_GTM_CONTAINER_ID`.
 *
 * Mixpanel is a first-party (device-mode) destination — events go from the
 * browser straight to Mixpanel, and the same SDK instance records Session
 * Replay, so replay properties ride along on events natively. Under Playwright,
 * the capture plugin is the only one loaded.
 */
export interface AnalyticsEnv {
  app: string;
  appVersion: string;
  mixpanelToken: string;
  gtmContainerId: string;
}

/**
 * The official `@analytics/mixpanel` plugin with its loader replaced.
 *
 * We keep everything that makes it the official integration — `track`,
 * `identify` (including `people.set` for traits), `reset`, `alias`, and its
 * `loaded: () => !!window.mixpanel` readiness gate — but swap out `initialize`,
 * which would otherwise inject a second, unpinned copy of the SDK from
 * `cdn.mxpnl.com`. See `plugins/mixpanelSdk.ts` for the full reasoning.
 *
 * Consequence worth knowing: SDK-level settings do NOT belong here. The
 * vendor's `options` is read only by the `initialize` we just replaced, so
 * anything passed to it silently does nothing. Set them on the real `mp.init()`
 * in `plugins/mixpanelSdk.ts` — the track-only proxy host included, via
 * `VITE_MIXPANEL_TRACK_API_HOST`.
 */
function createMixpanelPlugin(token: string): AnalyticsPlugin {
  return {
    ...mixpanelPlugin({ token }),
    initialize: () => {
      void bootstrapMixpanelSdk();
    },
  };
}

export function buildAnalyticsInstance(env: AnalyticsEnv): AnalyticsInstance {
  const plugins = [];

  if (IS_E2E) {
    plugins.push(createE2EPlugin());
  } else {
    if (env.mixpanelToken) {
      plugins.push(createMixpanelPlugin(env.mixpanelToken));
    }
    if (env.gtmContainerId) {
      plugins.push(googleTagManager({ containerId: env.gtmContainerId }));
    }
  }

  return Analytics({ app: env.app, version: env.appVersion, plugins });
}
